import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { notificationService } from '@/services/notification/notification.client';
import { CreateOrAssignBundle, CreateOrAssignBundleVariables } from
  '@/graphql/contracts/_generated_/createOrAssignBundle.mutation';
import CreateOrAssignBundleMutation from '@/graphql/contracts/createOrAssignBundle.mutation.gql';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { ALL_ITEMS } from '@/config/common.config';
import {
  clearLinesSelection,
  triggerLineRefresh,
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

export default function useCreateOrAssignBundle(contractId: UUID) {
  const [_createOrAssignBundle, { loading, data }] = useMutation<CreateOrAssignBundle, CreateOrAssignBundleVariables>(
    CreateOrAssignBundleMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      onCompleted: () => {
        clearLinesSelection();
        triggerLineRefresh();
        triggerVariableRefresh();
        notificationService.open({
          title: 'Success',
          text: 'Bundle created or assigned successfully',
          type: 'success'
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        },
        {
          query: GetPaginatedFilteredVariableContractLinesQuery,
          variables: {
            filter: { headerPrimaryKey: { eq: contractId } },
            first: ALL_ITEMS,
          }
        }
      ]
    }
  );

  const createOrAssignBundle = async (input: CreateOrAssignBundleVariables['input']) => {
    const result = await _createOrAssignBundle({
      variables: { input }
    });

    return result.data?.createOrAssignBundle;
  };

  return {
    createOrAssignBundle,
    createdOrAssignedBundle: data?.createOrAssignBundle,
    isLoadingCreateOrAssignBundle: loading,
  } as const;
}
